import { useEffect, useMemo, useRef, useState } from "react";
import {
    FaChevronDown,
    FaMagnifyingGlass,
    FaServer,
} from "react-icons/fa6";
import { useHookWaDevice } from "../../../hooks/useHookWaDevice";

export const SelectWaDevice = ({
    label,
    value, // Bisa berupa ID (string/number) atau Object Device
    setValue,
    onChange, // Akan menerima object device
    placeholder = "Pilih Device Whatsapp",
    searchable = true,
}) => {
    const { data } = useHookWaDevice();

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const wrapperRef = useRef(null);

    const safeData = Array.isArray(data) ? data : [];

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target)
            ) {
                setOpen(false);
                setSearch("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
        };
    }, []);

    // Update logika pencarian 'selected' agar support value berupa Object atau ID
    const selected = safeData.find((item) => {
        if (typeof value === "object" && value !== null) {
            return String(item.id) === String(value.id);
        }
        return String(item.id) === String(value);
    });

    const filteredOptions = useMemo(() => {
        return safeData
            .filter((item) =>
                `${item?.number ?? ""} ${item?.name ?? ""}`
                    .toLowerCase()
                    .includes(search.toLowerCase())
            )
            .sort((a, b) => {
                const aConnected =
                    a?.status?.toLowerCase() === "connected";
                const bConnected =
                    b?.status?.toLowerCase() === "connected";

                return Number(bConnected) - Number(aConnected);
            });
    }, [safeData, search]);

    // Fungsi handler utama
    const handleSelect = (selectedValue) => {
        setOpen(false);
        setSearch("");

        let payload = null;

        // Jika yang dipilih "Semua Device" (string kosong)
        if (selectedValue === "") {
            payload = null;
        } else {
            // Cari object lengkap dari data berdasarkan ID
            payload = safeData.find(
                (item) => String(item.id) === String(selectedValue)
            );
        }

        // Panggil onChange dengan object lengkap
        if (onChange) {
            onChange(payload);
        } else if (setValue) {
            setValue(payload);
        }
    };

    return (
        <div
            className="w-full relative"
            ref={wrapperRef}
        >
            {label && (
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                    {label}
                </label>
            )}

            {/* Select Trigger */}
            <div
                onClick={() => setOpen((prev) => !prev)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 cursor-pointer transition-all duration-200"
                style={{
                    borderColor: open
                        ? "rgba(0, 180, 216, 0.5)"
                        : "rgba(71, 85, 105, 0.4)",
                    boxShadow: open
                        ? "0 0 0 1px rgba(0, 180, 216, 0.2)"
                        : "none",
                }}
            >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FaServer className="text-slate-500 text-xs flex-shrink-0" />

                    <span
                        className={`truncate ${
                            selected
                                ? "text-gray-100"
                                : "text-slate-500"
                        }`}
                    >
                        {selected
                            ? `${selected?.phone_number} | ${
                                  selected?.name ?? "-"
                              }`
                            : placeholder}
                    </span>
                </div>

                <FaChevronDown
                    className={`text-xs text-slate-500 transition-transform duration-200 ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </div>

            {/* Dropdown Content */}
            {open && (
                <div
                    className="absolute z-[1000] mt-2 w-full rounded-xl overflow-hidden shadow-2xl shadow-black/40"
                    style={{
                        background:
                            "rgba(15, 23, 42, 0.95)",
                        border:
                            "1px solid rgba(51, 65, 85, 0.5)",
                        backdropFilter: "blur(16px)",
                    }}
                >
                    {/* Search Input */}
                    {searchable && (
                        <div
                            className="p-2.5"
                            style={{
                                borderBottom:
                                    "1px solid rgba(51, 65, 85, 0.3)",
                            }}
                        >
                            <div className="relative">
                                <FaMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />

                                <input
                                    autoFocus
                                    type="text"
                                    value={search}
                                    onChange={(e) =>
                                        setSearch(
                                            e.target.value
                                        )
                                    }
                                    placeholder="Cari Device Whatsapp..."
                                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-slate-800/80 border border-slate-600/30 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                />
                            </div>
                        </div>
                    )}

                    {/* List Options */}
                    <div className="max-h-64 overflow-y-auto py-1">
                        {/* Option: Semua Device */}
                        <div
                            onClick={() => handleSelect("")}
                            className={`px-3 py-2.5 mx-1 rounded-lg cursor-pointer text-sm transition-all duration-150 flex items-center gap-2 ${
                                !selected
                                    ? "bg-cyan-600/15 text-cyan-400 font-semibold"
                                    : "text-slate-400 hover:bg-slate-700/40 hover:text-gray-100"
                            }`}
                        >
                            <FaServer className="text-xs" />
                            <span>Semua Device</span>
                        </div>

                        {filteredOptions.length === 0 ? (
                            <div className="px-4 py-6 text-center text-slate-500 text-sm italic">
                                Device Whatsapp tidak ditemukan
                            </div>
                        ) : (
                            filteredOptions.map((opt) => {
                                const isDisabled = opt?.is_active == 0;

                                return (
                                    <div
                                        key={opt.id}
                                        onClick={() => {
                                            if (
                                                isDisabled
                                            )
                                                return;

                                            handleSelect(opt.id);
                                        }}
                                        className={`
                                            px-3 py-2.5 mx-1 rounded-lg text-sm transition-all duration-150
                                            flex items-center justify-between gap-2
                                            ${
                                                isDisabled
                                                    ? "opacity-50 cursor-not-allowed text-slate-500"
                                                    : selected?.id ===
                                                      opt.id
                                                    ? "bg-cyan-600/15 text-cyan-400 font-semibold cursor-pointer"
                                                    : "text-slate-300 hover:bg-slate-700/40 hover:text-gray-100 cursor-pointer"
                                            }
                                        `}
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FaServer className="text-xs opacity-50 flex-shrink-0" />

                                            <span className="truncate">
                                                {
                                                    opt?.phone_number
                                                }{" "}
                                                |{" "}
                                                {opt?.name ??
                                                    "-"}
                                            </span>
                                        </div>

                                        <span
                                            className={`px-2 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                                                isDisabled
                                                    ? "bg-red-500/15 text-red-400"
                                                    : "bg-emerald-500/15 text-emerald-400"
                                            }`}
                                        >
                                            {opt?.is_active === 1 ? "Terhubung" : "Terputus"}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};